import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  CircleParking,
  Calendar,
  Clock,
  Download,
  ExternalLink,
  Globe,
  Link2,
  Mail,
  MapPin,
  PlayCircle,
  Search,
  Smartphone,
  Undo2,
  UserCheck,
  UserPlus,
  UserX,
  UsersRound,
  X,
  Flag,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RecruitmentShell from "../components/RecruitmentShell";
import { HiringRoleStatusGlyph } from "../components/HiringRoleStatusGlyph";
import { HiringRoleApplicantStats } from "../components/RecruiterApplicantIcons";
import PitchVideoPlayer from "../components/PitchVideoPlayer";
import InviteApplicantsModal from "../components/InviteApplicantsModal";
import {
  getHiringJobPost,
  listHiringJobPostApplications,
  downloadHiringApplicantCv,
  patchHiringJobApplicationStatus,
  reportHiringJobApplicant,
  patchHiringJobPost,
} from "../apiClient";
import {
  HIRING_ROLE_STATUSES,
  daysFromToday,
  formatRoleDateDisplay,
  formatRoleDeadlineSubtitle,
  formatRolePublishedSubtitle,
  getLocalDateString,
  hiringRoleStatusLabel,
  hiringUiStatusFromJobPost,
  saveHiringRoleSettings,
} from "../data/hiringOpenRoles";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import JobuwinPageLoading from "../components/JobuwinPageLoading";
import { sortRejectionReasonTemplates } from "../data/rejectionReasonTemplates";
import { COUNTRIES } from "../data/countriesList";
import { useRejectionReasonTemplates } from "../hooks/useRejectionReasonTemplates";
import { resolveHiringCompanyName, resolveHiringManagerName } from "../lib/responseSettings";

const COUNTRY_LABEL_BY_ISO = new Map(COUNTRIES.map((country) => [country.value, country.label]));

const APPLICANT_ICON_FILTER_KEYS = ["new", "shortlisted", "parked", "rejected", "all"];
const APPLICANT_MOVE_FEEDBACK_MS = 700;
const APPLICANTS_LAZY_BATCH = 6;
const APPLICANT_REPORT_MIN_EXPLANATION_LENGTH = 10;

const APPLICANT_LIST_HEADINGS = {
  new: { label: "New applicants", icon: UserPlus },
  shortlisted: { label: "Shortlisted applicants", icon: UserCheck },
  parked: { label: "Parked applicants", icon: CircleParking },
  rejected: { label: "Rejected applicants", icon: UserX },
  all: { label: "All applicants", icon: UsersRound },
};

const applicantFieldIcons = {
  location: <MapPin size={18} strokeWidth={1.75} aria-hidden />,
  email: <Mail size={18} strokeWidth={1.75} aria-hidden />,
  mobile: <Smartphone size={18} strokeWidth={1.75} aria-hidden />,
  linkedin: (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      aria-hidden
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm5-1.775v.5" />
      <path d="M8 16.375V10.75m4 5.625V13.5m0 0v-2.75m0 2.75c0-1.288 1.222-2 2.4-2 1.6 0 1.6 1.375 1.6 2.875v2" />
    </svg>
  ),
  instagram: (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      aria-hidden
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 17.96 3 16.84 3 14.6zm14-2.9h.5" />
      <path d="M15.462 11.487a3.5 3.5 0 1 1-6.925 1.026 3.5 3.5 0 0 1 6.925-1.026" />
    </svg>
  ),
  link: <Link2 size={18} strokeWidth={1.75} aria-hidden />,
  youtube: <Globe size={18} strokeWidth={1.75} aria-hidden />,
};

const STAGE_ACTION_ICON_PROPS = { size: 22, strokeWidth: 1.75, "aria-hidden": true };

const APPLICANT_ACTION_OPTIONS = [
  {
    key: "shortlisted",
    label: "Shortlist",
    icon: <UserPlus {...STAGE_ACTION_ICON_PROPS} />,
  },
  {
    key: "parked",
    label: "Park",
    icon: <CircleParking {...STAGE_ACTION_ICON_PROPS} />,
  },
  {
    key: "rejected",
    label: "Reject",
    icon: <UserX {...STAGE_ACTION_ICON_PROPS} />,
  },
];

const PLACEHOLDER_REMOTE_ROLE = {
  id: "",
  status: "active",
  title: "",
  meta: "",
  expiryDate: null,
  publishedAt: null,
  listingJobId: "",
  applicants: 0,
  newApplications: 0,
  rejectedApplications: 0,
  shortlistedApplicants: 0,
  parkApplicants: 0,
};

function safeNonNegativeInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), 1_000_000_000);
}

function mapJobPostToRemoteRole(jobPost) {
  return {
    id: String(jobPost.id),
    status: hiringUiStatusFromJobPost(jobPost),
    title: jobPost.title || "",
    meta: jobPost.location || "",
    expiryDate: jobPost.deadline || null,
    publishedAt: jobPost.publishedAt || jobPost.published_at || null,
    listingJobId: String(jobPost.id),
    publicSlug: jobPost.publicSlug || jobPost.public_slug || "",
    applicants: safeNonNegativeInt(jobPost.applicants),
    newApplications: safeNonNegativeInt(jobPost.newApplications),
    rejectedApplications: safeNonNegativeInt(jobPost.rejectedApplications),
    shortlistedApplicants: safeNonNegativeInt(jobPost.shortlistedApplicants),
    parkApplicants: safeNonNegativeInt(jobPost.parkApplicants),
  };
}

function getInitialApplicantAction(applicant) {
  if (applicant?.statuses?.includes("rejected")) return "rejected";
  if (applicant?.statuses?.includes("shortlisted")) return "shortlisted";
  if (applicant?.statuses?.includes("parked")) return "parked";
  return "";
}

function statusesForCommittedApplicantAction(action, baseStatuses) {
  if (action === "rejected") return ["rejected", "all"];
  if (action === "shortlisted") return ["shortlisted", "all"];
  if (action === "parked") return ["parked", "all"];
  return [...baseStatuses];
}

function actionFromApplicationStatus(status) {
  if (status === "rejected" || status === "shortlisted" || status === "parked") return status;
  return "";
}

function applicantFilterIndexForAction(action) {
  if (action === "rejected") return APPLICANT_ICON_FILTER_KEYS.indexOf("rejected");
  if (action === "shortlisted") return APPLICANT_ICON_FILTER_KEYS.indexOf("shortlisted");
  if (action === "parked") return APPLICANT_ICON_FILTER_KEYS.indexOf("parked");
  return APPLICANT_ICON_FILTER_KEYS.indexOf("new");
}

function getApplicantProfileLinkCells(applicant) {
  const customs = Array.isArray(applicant?.customLinks) ? applicant.customLinks : [];
  const customCells = customs.map((link, idx) => ({
    key: `custom-${idx}-${link.label}`,
    label: link.label,
    href: link.url,
    icon: applicantFieldIcons.link,
  }));

  const cells = [
    { key: "linkedin", label: "LinkedIn", href: applicant.linkedin, icon: applicantFieldIcons.linkedin },
    { key: "instagram", label: "Instagram", href: applicant.instagram, icon: applicantFieldIcons.instagram },
    { key: "youtube", label: "YouTube", href: applicant.youtube, icon: applicantFieldIcons.youtube },
    ...customCells,
  ].filter((cell) => normalizeExternalHref(cell.href));

  return cells.slice(0, 6);
}

function sanitizeCvDownloadFileName(name) {
  const base = String(name || "").split(/[/\\]/).pop() || "";
  const cleaned = base.replace(/[^\w.\- ()]+/g, "_").trim();
  if (cleaned) return cleaned.length > 200 ? cleaned.slice(0, 200) : cleaned;
  return "Applicant-CV.pdf";
}

function triggerBrowserDownload(blob, suggestedName) {
  const filename = sanitizeCvDownloadFileName(suggestedName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

function ApplicantDetailLinksList({ applicant }) {
  const cells = getApplicantProfileLinkCells(applicant);
  if (cells.length === 0) return null;

  return (
    <div className="hiring-role-applicant-detail-links" aria-label="Applicant profile links">
      <p className="hiring-role-applicant-detail-links__heading">Links</p>
      <ul className="hiring-role-applicant-detail-links__list">
        {cells.map((cell) => {
          const url = normalizeExternalHref(cell.href);
          return (
            <li key={`${applicant.id}-detail-${cell.key}`}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="hiring-role-applicant-detail-link-row"
              >
                <span className="hiring-role-applicant-meta-icon" aria-hidden="true">
                  {cell.icon}
                </span>
                <span className="hiring-role-applicant-detail-link-row__label">{cell.label}</span>
                <ChevronRight size={18} strokeWidth={1.75} aria-hidden className="hiring-role-applicant-detail-link-row__chevron" />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ApplicantDetailProfile({
  applicant,
  roleTitle,
  onDownloadCv,
  cvDownloadBusy = false,
  applicantMoveFeedbackById,
  applicantRestoreFeedbackById,
}) {
  const stageBadge = applicantStageBadgeLabel(applicant);

  return (
    <article
      className={`hiring-role-applicant-detail-profile${
        applicantMoveFeedbackById[applicant.id] ? " hiring-role-applicant-detail-profile--moving" : ""
      }${applicantRestoreFeedbackById[applicant.id] ? " hiring-role-applicant-detail-profile--restored" : ""}`}
    >
      <aside className="hiring-role-applicant-detail-sidebar">
        <div className="hiring-role-applicant-detail-sidebar__identity">
          <div className="hiring-role-applicant-detail-sidebar__photo-wrap">
            <img
              src={applicant.photoDataUrl || profilePlaceholder}
              alt=""
              className="hiring-role-applicant-detail-sidebar__photo"
              width={176}
              height={176}
            />
            {stageBadge ? (
              <span
                className={`hiring-role-applicant-detail-sidebar__status-pill hiring-role-applicant-detail-sidebar__status-pill--${stageBadge.toLowerCase()}`}
              >
                {stageBadge}
              </span>
            ) : null}
          </div>
          <h2 className="hiring-role-applicant-detail-sidebar__name">{applicant.name}</h2>
          {roleTitle ? (
            <p className="hiring-role-applicant-detail-sidebar__role">{roleTitle}</p>
          ) : null}
        </div>
        <ul className="hiring-role-applicant-detail-sidebar__contact">
          <ApplicantDetailContactRow value={applicant.email} icon={applicantFieldIcons.email} />
          <ApplicantDetailContactRow
            value={formatApplicantMobile(applicant)}
            icon={applicantFieldIcons.mobile}
          />
          <ApplicantDetailContactRow
            value={formatApplicantLocations(applicant)}
            icon={applicantFieldIcons.location}
          />
          <ApplicantDetailContactRow
            value={formatApplicantAppliedSidebarPrimary(applicant)}
            icon={<Clock size={18} strokeWidth={1.75} />}
          />
        </ul>
        <ApplicantDetailLinksList applicant={applicant} />
        <button
          type="button"
          className="hiring-role-applicant-detail-sidebar__cv-btn"
          onClick={onDownloadCv}
          disabled={cvDownloadBusy}
        >
          <Download size={18} strokeWidth={1.75} aria-hidden />
          {cvDownloadBusy ? "Downloading CV…" : "Download CV"}
        </button>
      </aside>
      <div className="hiring-role-applicant-detail-main">
        <PitchVideoPlayer
          className="hiring-role-applicant-detail-pitch"
          src={applicant.pitchVideoUrl}
          ariaLabel={`${applicant.name} pitch video`}
          preload="metadata"
        />
        <ApplicantCvPreview
          applicant={applicant}
          detailModal
          onDownloadCv={onDownloadCv}
          cvDownloadBusy={cvDownloadBusy}
        />
      </div>
    </article>
  );
}

function ApplicantDetailBrowseNav({
  applicantTabIndex,
  totalApplicants,
  setApplicantTabIndex,
  onClose,
}) {
  const goPrev = () => setApplicantTabIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setApplicantTabIndex((prev) => Math.min(totalApplicants - 1, prev + 1));

  return (
    <header className="hiring-role-applicant-detail-modal__head">
      <div className="hiring-role-applicant-detail-modal__browse" role="toolbar" aria-label="Browse applicants">
        <button
          type="button"
          className="hiring-role-applicant-detail-modal__browse-btn"
          aria-label="Previous applicant"
          onClick={goPrev}
          disabled={applicantTabIndex === 0}
        >
          <ChevronLeft size={18} strokeWidth={1.75} aria-hidden />
          <span>Previous</span>
        </button>
        <p className="hiring-role-applicant-detail-modal__browse-count" aria-live="polite">
          {applicantTabIndex + 1} / {totalApplicants} applicants
        </p>
        <div className="hiring-role-applicant-detail-modal__browse-end">
          <button
            type="button"
            className="hiring-role-applicant-detail-modal__browse-btn"
            aria-label="Next applicant"
            onClick={goNext}
            disabled={applicantTabIndex >= totalApplicants - 1}
          >
            <span>Next</span>
            <ChevronRight size={18} strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            className="hiring-role-applicant-detail-modal__close"
            aria-label="Close applicant profile"
            onClick={onClose}
          >
            <X size={20} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}

function formatCountryLabel(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const iso = text.toUpperCase();
  if (/^[A-Z]{2}$/.test(iso)) {
    return COUNTRY_LABEL_BY_ISO.get(iso) || iso;
  }
  return text;
}

function formatApplicantLocations(applicant) {
  if (!applicant || typeof applicant !== "object") return "";

  const town = String(
    applicant.townCityOfResidence ?? applicant.town_city_of_residence ?? ""
  ).trim();
  const countryRaw = String(
    applicant.countryOfResidence ?? applicant.country_of_residence ?? ""
  ).trim();
  const country = formatCountryLabel(countryRaw);

  const fromParts = [town, country].filter(Boolean);
  if (fromParts.length > 0) return fromParts.join(", ");

  const list = Array.isArray(applicant.locations)
    ? applicant.locations
        .map((item, index, items) => {
          const value = String(item || "").trim();
          if (!value) return "";
          const isLast = index === items.length - 1;
          if (isLast && items.length > 1 && /^[A-Za-z]{2}$/.test(value)) {
            return formatCountryLabel(value);
          }
          return value;
        })
        .filter(Boolean)
        .slice(0, 3)
    : [];
  if (list.length > 0) return list.join(", ");

  const direct = String(applicant.location || "").trim();
  if (!direct) return "";

  const segments = direct.split(",").map((part) => part.trim()).filter(Boolean);
  if (segments.length === 0) return direct;
  if (segments.length === 1) return formatCountryLabel(segments[0]) || segments[0];

  const last = segments[segments.length - 1];
  if (/^[A-Za-z]{2}$/.test(last)) {
    segments[segments.length - 1] = formatCountryLabel(last);
  }
  return segments.join(", ");
}

function formatApplicantMobile(applicant) {
  const countryCode = String(applicant?.countryCode || "").trim();
  const mobile = String(applicant?.mobile || "").trim();
  if (!countryCode) return mobile;
  if (!mobile) return countryCode;
  if (mobile.startsWith("+")) return mobile;
  return `${countryCode} ${mobile}`;
}

function formatVideoDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return "";
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function applicantAppliedAtMs(applicant) {
  const raw = applicant?.appliedAt ?? applicant?.applied_at;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function sortApplicantsFirstAppliedFirst(applicants) {
  return [...applicants].sort((a, b) => {
    const diff = applicantAppliedAtMs(a) - applicantAppliedAtMs(b);
    if (diff !== 0) return diff;
    return String(a.id).localeCompare(String(b.id));
  });
}

function formatApplicantAppliedAtLabel(applicant) {
  const ms = applicantAppliedAtMs(applicant);
  if (!ms) return "Applied recently";
  const when = new Date(ms).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `Applied ${when}`;
}

function formatApplicantAppliedSidebarPrimary(applicant) {
  const ms = applicantAppliedAtMs(applicant);
  if (!ms) return "Applied recently";

  const diffMs = Date.now() - ms;
  if (diffMs < 0) return formatApplicantAppliedAtLabel(applicant);

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Applied just now";
  if (minutes < 60) {
    return `Applied ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `Applied ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 14) {
    return `Applied ${days} day${days === 1 ? "" : "s"} ago`;
  }

  const when = new Date(ms).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `Applied ${when}`;
}

function ApplicantDetailContactRow({ icon, value, subvalue }) {
  if (!value && !subvalue) return null;

  return (
    <li className="hiring-role-applicant-detail-sidebar__contact-row">
      <span className="hiring-role-applicant-detail-sidebar__contact-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="hiring-role-applicant-detail-sidebar__contact-text">
        {value ? <span className="hiring-role-applicant-detail-sidebar__contact-line">{value}</span> : null}
        {subvalue ? (
          <span className="hiring-role-applicant-detail-sidebar__contact-subline">{subvalue}</span>
        ) : null}
      </span>
    </li>
  );
}

function applicantStageBadgeLabel(applicant) {
  if (applicant?.statuses?.includes("rejected")) return "Rejected";
  if (applicant?.statuses?.includes("shortlisted")) return "Shortlisted";
  if (applicant?.statuses?.includes("parked")) return "Parked";
  if (applicant?.statuses?.includes("new")) return "New";
  return "";
}

function ApplicantPitchThumbnail({ src, listVariant = false, onClick, playLabel = "Play pitch video" }) {
  const videoRef = useRef(null);
  const [durationLabel, setDurationLabel] = useState("");
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    setDurationLabel("");
    setFrameReady(false);
  }, [src]);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration)) {
      setDurationLabel(formatVideoDuration(video.duration));
    }
    const seekTo = Number.isFinite(video.duration) ? Math.min(1, video.duration * 0.08) : 0.1;
    if (video.currentTime < 0.01) {
      video.currentTime = seekTo;
    }
  };

  const handleSeeked = () => {
    setFrameReady(true);
  };

  const thumbClassName = `hiring-role-applicant-pitch-thumb${
    listVariant ? " hiring-role-applicant-pitch-thumb--list" : ""
  }${onClick && src ? " hiring-role-applicant-pitch-thumb--interactive" : ""}`;

  if (!src) {
    return (
      <div
        className={`${thumbClassName} hiring-role-applicant-pitch-thumb--empty`}
        aria-hidden="true"
      >
        <PlayCircle size={32} strokeWidth={1.75} />
      </div>
    );
  }

  const thumbContent = (
    <>
      <video
        ref={videoRef}
        className={`hiring-role-applicant-pitch-thumb__video${
          frameReady ? " hiring-role-applicant-pitch-thumb__video--ready" : ""
        }`}
        src={src}
        preload="metadata"
        muted
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onSeeked={handleSeeked}
      />
      <span className="hiring-role-applicant-pitch-thumb__play" aria-hidden="true">
        <PlayCircle size={listVariant ? 44 : 32} strokeWidth={1.75} />
      </span>
      {durationLabel ? (
        <span className="hiring-role-applicant-pitch-thumb__duration">{durationLabel}</span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={thumbClassName}
        aria-label={playLabel}
        onClick={(event) => {
          event.stopPropagation();
          onClick(event);
        }}
      >
        {thumbContent}
      </button>
    );
  }

  return (
    <div className={thumbClassName} aria-hidden="true">
      {thumbContent}
    </div>
  );
}

function useModalBodyScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

function ApplicantPitchLightbox({ applicant, onClose }) {
  useModalBodyScrollLock();
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!applicant?.pitchVideoUrl) return null;

  return (
    <div
      className="hiring-role-applicant-pitch-lightbox"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="hiring-role-applicant-pitch-lightbox__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hiring-role-pitch-lightbox-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="hiring-role-applicant-pitch-lightbox__head">
          <h2 id="hiring-role-pitch-lightbox-title" className="hiring-role-applicant-pitch-lightbox__title">
            {applicant.name}&apos;s pitch
          </h2>
          <button
            type="button"
            className="hiring-role-applicant-pitch-lightbox__close"
            aria-label="Close video"
            onClick={onClose}
          >
            <X size={20} strokeWidth={1.75} aria-hidden />
          </button>
        </header>
        <div className="hiring-role-applicant-pitch-lightbox__body">
          <PitchVideoPlayer
            className="hiring-role-applicant-pitch-lightbox__player"
            src={applicant.pitchVideoUrl}
            ariaLabel={`${applicant.name} pitch video`}
            preload="auto"
            autoplay
          />
        </div>
      </div>
    </div>
  );
}

function ApplicantListCompactMeta({ icon, value }) {
  if (!value) return null;
  return (
    <p className="hiring-role-applicant-list-meta">
      <span className="hiring-role-applicant-list-meta__icon" aria-hidden="true">
        {icon}
      </span>
      <span title={value}>{value}</span>
    </p>
  );
}

function normalizeExternalHref(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^mailto:/i.test(s)) return s;
  if (/^tel:/i.test(s)) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//.test(s)) return `https:${s}`;
  return `https://${s}`;
}

function mapHiringApplicantFromApi(a) {
  if (!a || typeof a !== "object") return null;
  let rawSocial = a.socialProfiles ?? a.social_profiles;
  if (typeof rawSocial === "string") {
    try {
      rawSocial = JSON.parse(rawSocial);
    } catch {
      rawSocial = null;
    }
  }
  const social =
    rawSocial && typeof rawSocial === "object" && !Array.isArray(rawSocial) ? rawSocial : {};
  const customsFromApi = Array.isArray(a.customLinks)
    ? a.customLinks
    : Array.isArray(a.custom_links)
      ? a.custom_links
      : [];
  const customsFromSocial = Array.isArray(social.customs) ? social.customs : [];
  const rawCustom =
    customsFromApi.length > 0
      ? customsFromApi
      : customsFromSocial.map((c) => ({
          label: String(c.platformName ?? c.name ?? "Link").trim() || "Link",
          url: String(c.url ?? c.link ?? "").trim(),
        }));
  const customLinks = rawCustom
    .map((row) => ({
      label: String(row.label ?? row.platformName ?? "Link").trim() || "Link",
      url: String(row.url ?? "").trim(),
    }))
    .filter((row) => row.url);

  const townCityOfResidence = String(
    a.townCityOfResidence ?? a.town_city_of_residence ?? ""
  ).trim();
  const countryOfResidence = String(
    a.countryOfResidence ?? a.country_of_residence ?? ""
  )
    .trim()
    .toUpperCase();
  const locationParts = [townCityOfResidence, countryOfResidence].filter(Boolean);
  const locations =
    Array.isArray(a.locations) && a.locations.some((item) => String(item || "").trim())
      ? a.locations
      : locationParts;
  const location = String(a.location || "").trim() || locationParts.join(", ");

  return {
    ...a,
    id: String(a.id),
    countryCode: a.countryCode || a.country_code || "",
    townCityOfResidence,
    countryOfResidence,
    location,
    locations,
    photoDataUrl: a.profilePhotoUrl || a.profile_photo_url || "",
    pitchVideoUrl: a.pitchVideoUrl || a.pitch_video_url || "",
    cvFileName: String(a.cvFileName ?? a.cv_file_name ?? "").trim(),
    appliedAt: a.appliedAt || a.applied_at || null,
    statuses: Array.isArray(a.statuses) ? a.statuses : [],
    linkedin: String(a.linkedin ?? social.linkedin ?? "").trim(),
    instagram: String(a.instagram ?? social.instagram ?? "").trim(),
    youtube: String(a.youtube ?? social.youtube ?? "").trim(),
    customLinks,
  };
}

function ApplicantLinkCell({ icon, label, href, listCard = false }) {
  const url = normalizeExternalHref(href);
  if (!url) return null;
  const isHttp = /^https?:\/\//i.test(url) || /^\/\//.test(url);
  return (
    <a
      href={url}
      {...(isHttp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={`hiring-role-applicant-link-pill${
        listCard ? " hiring-role-applicant-list-card__link" : ""
      }`}
      onClick={(event) => event.stopPropagation()}
    >
      <span className="hiring-role-applicant-meta-icon" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </a>
  );
}

function ApplicantProfileLinksRow({ applicant, listVariant = false }) {
  const visibleCells = getApplicantProfileLinkCells(applicant);
  if (visibleCells.length === 0) return null;

  if (listVariant) {
    return (
      <div className="hiring-role-applicant-list-card__links-grid" aria-label="Applicant profile links">
        {visibleCells.map((cell) => (
          <ApplicantLinkCell
            key={`${applicant.id}-${cell.key}`}
            label={cell.label}
            href={cell.href}
            icon={cell.icon}
            listCard
          />
        ))}
      </div>
    );
  }

  return (
    <div className="hiring-role-applicant-card-links" aria-label="Applicant profile links">
      <div className="hiring-role-applicant-links-grid">
        {visibleCells.map((cell) => (
          <ApplicantLinkCell
            key={`${applicant.id}-${cell.key}`}
            label={cell.label}
            href={cell.href}
            icon={cell.icon}
          />
        ))}
      </div>
    </div>
  );
}

function ApplicantCvDocumentPages({ applicant }) {
  if (!applicant) return null;
  const pageNumbers = [1, 2, 3, 4];
  return (
    <div className="hiring-role-applicant-cv-pdf" role="region" aria-label="Applicant CV PDF preview">
      {pageNumbers.map((page) => (
        <div key={`${applicant.id}-cv-page-${page}`} className="hiring-role-applicant-cv-pdf-page">
          <p className="hiring-role-applicant-cv-page-number">Page {page} of {pageNumbers.length}</p>
          {page === 1 ? (
            <>
              <p>
                <strong>{applicant.name}</strong>
              </p>
              <p>{formatApplicantLocations(applicant)}</p>
              <p>{applicant.email}</p>
              <p>{formatApplicantMobile(applicant)}</p>
              <p>
                <strong>Professional Summary</strong>
              </p>
              <p>
                Experienced customer-focused professional with strong communication, stakeholder management, and
                problem-solving skills. Comfortable working in fast-paced, cross-functional teams while delivering
                measurable service outcomes.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>Experience (continued)</strong>
              </p>
              <p>
                Placeholder CV content for page {page}. This simulates a multi-page PDF preview with A4-sized pages
                in a scrollable document container.
              </p>
              <p>
                Led customer onboarding, retention, and escalation workflows; collaborated with product and
                operations teams to improve service quality and reduce response times.
              </p>
              <p>
                Built reporting habits around stakeholder updates, quality KPIs, and improvement actions across the
                customer lifecycle.
              </p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ApplicantCvPreview({ applicant, detailModal = false, onDownloadCv, cvDownloadBusy = false }) {
  if (!applicant) return null;
  return (
    <section
      className={`hiring-role-applicant-cv-preview${
        detailModal ? " hiring-role-applicant-cv-preview--detail-modal" : ""
      }`}
      aria-label="Applicant CV content preview"
    >
      <div className="hiring-role-applicant-cv-head">
        <h4>Applicant CV</h4>
        <div className="hiring-role-applicant-cv-actions">
          <span className="hiring-role-applicant-cv-file">{applicant.cvFileName || "Applicant-CV.pdf"}</span>
          <button
            type="button"
            className="small-outline-button hiring-role-applicant-cv-download-btn"
            onClick={onDownloadCv}
            disabled={cvDownloadBusy || !onDownloadCv}
          >
            <Download size={18} strokeWidth={1.75} aria-hidden />
            {cvDownloadBusy ? "Downloading CV…" : "Download CV"}
          </button>
        </div>
      </div>
      <ApplicantCvDocumentPages applicant={applicant} />
    </section>
  );
}

/** Hiring-side workspace for a published/hidden/closed role — applicants, listing link, deadline & visibility. */
export default function HiringRoleManagePage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId } = useParams();
  const profilePhotoSrc = currentUser?.photoDataUrl || profilePlaceholder;
  const { templates: rejectionReasonTemplates } = useRejectionReasonTemplates(authToken);
  const rejectReasonOptions = useMemo(
    () =>
      sortRejectionReasonTemplates(rejectionReasonTemplates)
        .map((t) => t.label.trim())
        .filter(Boolean),
    [rejectionReasonTemplates]
  );
  const inviteHiringManagerName = useMemo(() => resolveHiringManagerName(currentUser), [currentUser]);
  const inviteCompanyName = useMemo(() => resolveHiringCompanyName(currentUser), [currentUser]);

  const isUuidRoleId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(roleId || "")
  );
  const [remoteRole, setRemoteRole] = useState(null);
  const [remoteRoleError, setRemoteRoleError] = useState("");
  const role = isUuidRoleId ? remoteRole || { ...PLACEHOLDER_REMOTE_ROLE, id: String(roleId || "") } : null;

  useEffect(() => {
    if (!isUuidRoleId) return;
    const t = String(authToken || "").trim();
    if (!t) return;
    let cancelled = false;
    (async () => {
      try {
        setRemoteRoleError("");
        setRemoteRole(null);
        const { jobPost } = await getHiringJobPost(String(roleId), t);
        if (cancelled) return;
        if (jobPost?.status === "draft") {
          navigate(`/hiring/jobs/new?draft=${encodeURIComponent(String(jobPost.id))}`, { replace: true });
          return;
        }
        setRemoteRole(mapJobPostToRemoteRole(jobPost));
      } catch (e) {
        if (cancelled) return;
        setRemoteRoleError(e instanceof Error ? e.message : "Could not load role.");
        setRemoteRole(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, isUuidRoleId, navigate, roleId]);

  const [roleStatus, setRoleStatus] = useState("active");
  const [savedRoleStatus, setSavedRoleStatus] = useState("active");
  const [expiryDate, setExpiryDate] = useState(() => getLocalDateString());
  const [closedSaved, setClosedSaved] = useState(false);
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [applicantStatusesById, setApplicantStatusesById] = useState({});

  useEffect(() => {
    setSettingsHydrated(false);
  }, [roleId]);

  useEffect(() => {
    if (!isUuidRoleId || !remoteRole || settingsHydrated) return;
    const deadline = remoteRole.expiryDate || getLocalDateString();
    setRoleStatus(remoteRole.status);
    setSavedRoleStatus(remoteRole.status);
    setExpiryDate(deadline);
    setClosedSaved(remoteRole.status === "closed");
    setSettingsHydrated(true);
  }, [isUuidRoleId, remoteRole, settingsHydrated]);

  useEffect(() => {
    if (!isUuidRoleId) return;
    setApplicantStatusesById({});
    setApplicantActionSavingById({});
    setApplicantActionErrorById({});
    setApplicantMoveFeedbackById({});
    setApplicantRestoreFeedbackById({});
    setApplicantStatsFlashIndex(null);
    setLastApplicantMove(null);
    setUndoApplicantMoveSaving(false);
    setSaveNotice(null);
    setShowCloseWarning(false);
  }, [isUuidRoleId, roleId]);

  const isRemoteLoading = isUuidRoleId && !remoteRole && !remoteRoleError;
  if (!isUuidRoleId) return <Navigate to="/hiring-dashboard" replace />;

  const metaLine = (() => {
    if (role.status === "draft") {
      return (role.meta || "").replace(/\s*[-–—]\s*Posted\s+[^-–—]*$/i, "").trim();
    }
    const postedDaysAgo = typeof role.postedDaysAgo === "number" ? role.postedDaysAgo : null;
    if (postedDaysAgo == null) return role.meta;
    const postedOnIso = daysFromToday(-postedDaysAgo);
    const postedOnLabel = formatRoleDateDisplay(postedOnIso);
    return role.meta.replace(/Posted\s+(\d+)\s+(day|days|week|weeks)\s+ago/i, (match) => `${match} on ${postedOnLabel}`);
  })();

  const statusLabel = hiringRoleStatusLabel(roleStatus);
  const listingJobId = role.listingJobId;
  const publishedSubtitle = role.publishedAt ? formatRolePublishedSubtitle(role.publishedAt) : "";
  const expirySubtitle = expiryDate ? formatRoleDeadlineSubtitle(expiryDate) : "";
  const hasViewJobLink = Boolean(listingJobId || isUuidRoleId);
  const hasSubtitleMeta = Boolean(metaLine || publishedSubtitle || expirySubtitle);
  const isLocked = closedSaved && roleStatus === "closed";
  const isRoleStatusDirty = roleStatus !== savedRoleStatus;

  const persistVisibilityToServer = async (nextRoleStatus) => {
    const t = String(authToken || "").trim();
    if (!t) throw new Error("Not signed in.");
    const id = String(role?.id || "").trim();
    if (!id) throw new Error("Missing role id.");
    if (nextRoleStatus === "closed") {
      return patchHiringJobPost(id, { lifecycle: "archive" }, t);
    }
    const listed = nextRoleStatus === "active";
    return patchHiringJobPost(id, { listedInApplicantSearch: listed }, t);
  };

  const commitRoleSettings = async (nextRoleStatus, nextExpiryDate) => {
    const { jobPost } = await persistVisibilityToServer(nextRoleStatus);
    const nextClosedSaved = nextRoleStatus === "closed";
    saveHiringRoleSettings(role.id, {
      roleStatus: nextRoleStatus,
      expiryDate: nextExpiryDate,
      closedSaved: nextClosedSaved,
    });
    setRoleStatus(nextRoleStatus);
    setSavedRoleStatus(nextRoleStatus);
    setClosedSaved(nextClosedSaved);
    setRemoteRole(mapJobPostToRemoteRole(jobPost));
    setSaveNotice({ type: "success", message: "Saved" });
  };

  const onSubmitSettings = async (event) => {
    event.preventDefault();
    const trimmedExpiry = expiryDate.trim();
    if (!trimmedExpiry) {
      setSaveNotice({ type: "error", message: "Please set an application deadline." });
      return;
    }
    if (roleStatus === "closed" && savedRoleStatus !== "closed") {
      setSaveNotice(null);
      setShowCloseWarning(true);
      return;
    }
    setSettingsSaving(true);
    setSaveNotice(null);
    try {
      await commitRoleSettings(roleStatus, trimmedExpiry);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save status.";
      setSaveNotice({ type: "error", message: msg });
    } finally {
      setSettingsSaving(false);
    }
  };

  const statusDescriptions = {
    active: "Job is live and accepting applications.",
    hidden: "Job is hidden from applicants.",
    closed: "Job is closed and not accepting applications.",
  };
  const handleRoleStatusChange = (nextStatus) => {
    if (isLocked) return;
    setShowCloseWarning(false);
    setRoleStatus(nextStatus);
    if (nextStatus !== "closed") setClosedSaved(false);
  };
  const confirmCloseRole = async () => {
    const trimmedExpiry = expiryDate.trim();
    if (!trimmedExpiry) {
      setSaveNotice({ type: "error", message: "Please set an application deadline." });
      setShowCloseWarning(false);
      return;
    }
    setShowCloseWarning(false);
    setSettingsSaving(true);
    setSaveNotice(null);
    try {
      await commitRoleSettings("closed", trimmedExpiry);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save status.";
      setSaveNotice({ type: "error", message: msg });
    } finally {
      setSettingsSaving(false);
    }
  };
  const cancelCloseRole = () => {
    setShowCloseWarning(false);
  };

  const selectedApplicantIndex =
    typeof location.state?.selectedApplicantIndex === "number" ? location.state.selectedApplicantIndex : 0;
  const [activeApplicantIconIndex, setActiveApplicantIconIndex] = useState(selectedApplicantIndex);
  const [detailApplicantIndex, setDetailApplicantIndex] = useState(null);
  const [applicantActions, setApplicantActions] = useState({});
  const [applicantRejectReasons, setApplicantRejectReasons] = useState({});
  const [applicantActionSavingById, setApplicantActionSavingById] = useState({});
  const [applicantActionErrorById, setApplicantActionErrorById] = useState({});
  const [applicantMoveFeedbackById, setApplicantMoveFeedbackById] = useState({});
  const [applicantRestoreFeedbackById, setApplicantRestoreFeedbackById] = useState({});
  const [applicantStatsFlashIndex, setApplicantStatsFlashIndex] = useState(null);
  const [lastApplicantMove, setLastApplicantMove] = useState(null);
  const [undoApplicantMoveSaving, setUndoApplicantMoveSaving] = useState(false);
  const [applicantNameQueryDraft, setApplicantNameQueryDraft] = useState("");
  const [applicantNameQuery, setApplicantNameQuery] = useState("");
  const [visibleApplicantCount, setVisibleApplicantCount] = useState(APPLICANTS_LAZY_BATCH);
  const [rejectModalApplicantId, setRejectModalApplicantId] = useState(null);
  const [reportModalApplicantId, setReportModalApplicantId] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteModalInitialTab, setInviteModalInitialTab] = useState(null);
  const [draftRejectReason, setDraftRejectReason] = useState("");
  const [draftReportExplanation, setDraftReportExplanation] = useState("");
  const [sendRejectionEmail, setSendRejectionEmail] = useState(true);
  const [pitchLightboxApplicant, setPitchLightboxApplicant] = useState(null);
  const [cvDownloadBusyById, setCvDownloadBusyById] = useState({});

  useEffect(() => {
    setActiveApplicantIconIndex(selectedApplicantIndex);
  }, [selectedApplicantIndex, role.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") !== "invites") return;
    setInviteModalInitialTab("invited-log");
    setInviteModalOpen(true);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  const [roleApplicants, setRoleApplicants] = useState([]);
  const [applicantsListLoading, setApplicantsListLoading] = useState(false);
  const [applicantsListError, setApplicantsListError] = useState("");

  useEffect(() => {
    if (!isUuidRoleId || !roleId) return;
    const t = String(authToken || "").trim();
    if (!t) return;
    let cancelled = false;
    setRoleApplicants([]);
    setApplicantsListLoading(true);
    setApplicantsListError("");
    (async () => {
      try {
        const { applicants } = await listHiringJobPostApplications(String(roleId), t);
        if (cancelled) return;
        setRoleApplicants((applicants || []).map(mapHiringApplicantFromApi).filter(Boolean));
      } catch (e) {
        if (cancelled) return;
        setApplicantsListError(e instanceof Error ? e.message : "Could not load applicants.");
        setRoleApplicants([]);
      } finally {
        if (!cancelled) setApplicantsListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, isUuidRoleId, roleId]);

  const applicantFilterKey = APPLICANT_ICON_FILTER_KEYS[activeApplicantIconIndex] || "all";
  const workspaceApplicants = useMemo(
    () =>
      roleApplicants.map((applicant) => {
        const committed = applicantStatusesById[applicant.id];
        return committed ? { ...applicant, statuses: committed } : applicant;
      }),
    [roleApplicants, applicantStatusesById],
  );
  const roleForApplicantStats = useMemo(
    () => {
      if (applicantsListLoading && workspaceApplicants.length === 0) {
        return {
          ...role,
          applicants: safeNonNegativeInt(role.applicants),
          newApplications: safeNonNegativeInt(role.newApplications),
          rejectedApplications: safeNonNegativeInt(role.rejectedApplications),
          shortlistedApplicants: safeNonNegativeInt(role.shortlistedApplicants),
          parkApplicants: safeNonNegativeInt(role.parkApplicants),
        };
      }
      const counts = workspaceApplicants.reduce(
        (acc, applicant) => {
          const statuses = Array.isArray(applicant.statuses) ? applicant.statuses : [];
          if (statuses.includes("new")) acc.newApplications += 1;
          if (statuses.includes("rejected")) acc.rejectedApplications += 1;
          if (statuses.includes("shortlisted")) acc.shortlistedApplicants += 1;
          if (statuses.includes("parked")) acc.parkApplicants += 1;
          return acc;
        },
        {
          newApplications: 0,
          rejectedApplications: 0,
          shortlistedApplicants: 0,
          parkApplicants: 0,
        },
      );
      return {
        ...role,
        applicants: workspaceApplicants.length,
        ...counts,
      };
    },
    [applicantsListLoading, role, workspaceApplicants],
  );
  const filteredApplicants = useMemo(() => {
    const byStage = workspaceApplicants.filter((applicant) =>
      applicant.statuses.includes(applicantFilterKey),
    );
    const query = applicantNameQuery.trim().toLowerCase();
    const byName =
      query.length === 0
        ? byStage
        : byStage.filter((applicant) => String(applicant.name || "").toLowerCase().includes(query));
    return sortApplicantsFirstAppliedFirst(byName);
  }, [applicantFilterKey, applicantNameQuery, workspaceApplicants]);

  useEffect(() => {
    setApplicantNameQuery("");
    setApplicantNameQueryDraft("");
  }, [applicantFilterKey, roleId]);

  useEffect(() => {
    setVisibleApplicantCount(APPLICANTS_LAZY_BATCH);
  }, [applicantFilterKey, applicantNameQuery, roleId]);

  const applyApplicantNameSearch = useCallback(() => {
    setApplicantNameQuery(applicantNameQueryDraft.trim());
  }, [applicantNameQueryDraft]);

  const visibleApplicants = useMemo(
    () => filteredApplicants.slice(0, visibleApplicantCount),
    [filteredApplicants, visibleApplicantCount],
  );
  const hasMoreApplicants = visibleApplicantCount < filteredApplicants.length;
  const loadMoreSentinelRef = useRef(null);
  const visibleApplicantCountRef = useRef(visibleApplicantCount);
  const filteredApplicantsLengthRef = useRef(filteredApplicants.length);

  visibleApplicantCountRef.current = visibleApplicantCount;
  filteredApplicantsLengthRef.current = filteredApplicants.length;

  const loadMoreApplicants = useCallback(() => {
    setVisibleApplicantCount((prev) => {
      if (prev >= filteredApplicants.length) return prev;
      return Math.min(prev + APPLICANTS_LAZY_BATCH, filteredApplicants.length);
    });
  }, [filteredApplicants.length]);

  const loadMoreApplicantsRef = useRef(loadMoreApplicants);
  loadMoreApplicantsRef.current = loadMoreApplicants;

  const loadMoreObserverRef = useRef(null);

  const attachLoadMoreObserver = useCallback(
    (node) => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
        loadMoreObserverRef.current = null;
      }
      loadMoreSentinelRef.current = node;
      if (!node || applicantsListLoading || filteredApplicants.length === 0) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          if (visibleApplicantCountRef.current >= filteredApplicantsLengthRef.current) return;
          loadMoreApplicantsRef.current();
        },
        { root: null, rootMargin: "320px 0px", threshold: 0 },
      );

      observer.observe(node);
      loadMoreObserverRef.current = observer;
    },
    [applicantsListLoading, filteredApplicants.length, applicantFilterKey, applicantNameQuery],
  );

  useEffect(
    () => () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
        loadMoreObserverRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (applicantsListLoading || visibleApplicantCount >= filteredApplicants.length) return undefined;

    const node = loadMoreSentinelRef.current;
    if (!node) return undefined;

    const margin = 320;
    const frame = requestAnimationFrame(() => {
      const rect = node.getBoundingClientRect();
      const inView = rect.top <= window.innerHeight + margin && rect.bottom >= -margin;
      if (inView && visibleApplicantCountRef.current < filteredApplicantsLengthRef.current) {
        loadMoreApplicantsRef.current();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [visibleApplicantCount, applicantsListLoading, filteredApplicants.length]);

  useEffect(() => {
    setApplicantActions((prev) => {
      const next = {};
      roleApplicants.forEach((applicant) => {
        next[applicant.id] = prev[applicant.id] ?? getInitialApplicantAction(applicant);
      });
      return next;
    });
  }, [roleApplicants]);

  useEffect(() => {
    setDetailApplicantIndex(null);
  }, [applicantFilterKey]);

  useEffect(() => {
    if (detailApplicantIndex == null) return;
    if (filteredApplicants.length === 0 || detailApplicantIndex >= filteredApplicants.length) {
      setDetailApplicantIndex(null);
    }
  }, [detailApplicantIndex, filteredApplicants.length]);

  const detailApplicant =
    detailApplicantIndex != null ? filteredApplicants[detailApplicantIndex] || null : null;

  useEffect(() => {
    if (detailApplicantIndex == null) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (rejectModalApplicantId || reportModalApplicantId) return;
      setDetailApplicantIndex(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [detailApplicantIndex, rejectModalApplicantId, reportModalApplicantId]);

  const openRejectReasonModal = (applicantId) => {
    setRejectModalApplicantId(applicantId);
    setDraftRejectReason(applicantRejectReasons[applicantId] || rejectReasonOptions[0] || "");
    setSendRejectionEmail(true);
  };
  const openReportApplicantModal = (applicantId) => {
    setReportModalApplicantId(applicantId);
    setDraftReportExplanation("");
    setApplicantActionErrorById((prev) => {
      const next = { ...prev };
      delete next[applicantId];
      return next;
    });
  };

  const handleDownloadApplicantCv = async (applicant) => {
    if (!applicant?.id || cvDownloadBusyById[applicant.id]) return;
    const t = String(authToken || "").trim();
    const jobPostId = String(roleId || "").trim();
    if (!t || !jobPostId) return;

    setCvDownloadBusyById((prev) => ({ ...prev, [applicant.id]: true }));
    try {
      const { blob, fileName } = await downloadHiringApplicantCv(jobPostId, applicant.id, t);
      triggerBrowserDownload(blob, fileName || applicant.cvFileName || "Applicant-CV.pdf");
    } catch (e) {
      setApplicantActionErrorById((prev) => ({
        ...prev,
        [applicant.id]: e instanceof Error ? e.message : "Couldn't download the CV. Try again in a moment.",
      }));
    } finally {
      setCvDownloadBusyById((prev) => {
        const next = { ...prev };
        delete next[applicant.id];
        return next;
      });
    }
  };
  const closeRejectReasonModal = () => {
    setRejectModalApplicantId(null);
    setDraftRejectReason("");
    setSendRejectionEmail(false);
  };
  const closeReportApplicantModal = () => {
    setReportModalApplicantId(null);
    setDraftReportExplanation("");
  };

  useEffect(() => {
    if (!rejectModalApplicantId) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (applicantActionSavingById[rejectModalApplicantId]) return;
      event.preventDefault();
      event.stopPropagation();
      closeRejectReasonModal();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [rejectModalApplicantId, applicantActionSavingById]);

  const applyApplicantActionLocally = (applicantId, action) => {
    const nextStatus = action || "new";
    const nextStatuses = statusesForCommittedApplicantAction(action, ["new", "all"]);
    setApplicantActions((prev) => ({ ...prev, [applicantId]: action }));
    setApplicantMoveFeedbackById((prev) => {
      if (!prev[applicantId]) return prev;
      const next = { ...prev };
      delete next[applicantId];
      return next;
    });
    setApplicantStatusesById((prev) => ({ ...prev, [applicantId]: nextStatuses }));
    setRoleApplicants((prev) =>
      prev.map((applicant) =>
        applicant.id === applicantId
          ? {
              ...applicant,
              applicationStatus: nextStatus,
              statuses: nextStatuses,
            }
          : applicant,
      ),
    );
  };

  const flashApplicantDestination = (action) => {
    const flashIndex = applicantFilterIndexForAction(action);
    setApplicantStatsFlashIndex(flashIndex);
    window.setTimeout(() => {
      setApplicantStatsFlashIndex((current) => (current === flashIndex ? null : current));
    }, APPLICANT_MOVE_FEEDBACK_MS);
  };

  const finishApplicantMoveWithFeedback = (applicantId, action) => {
    const nextStatuses = statusesForCommittedApplicantAction(action, ["new", "all"]);
    const shouldExitCurrentList = applicantFilterKey !== "all" && !nextStatuses.includes(applicantFilterKey);

    flashApplicantDestination(action);

    if (!shouldExitCurrentList) {
      applyApplicantActionLocally(applicantId, action);
      return;
    }

    setApplicantMoveFeedbackById((prev) => ({ ...prev, [applicantId]: action || "new" }));
    window.setTimeout(() => {
      applyApplicantActionLocally(applicantId, action);
    }, APPLICANT_MOVE_FEEDBACK_MS);
  };

  const finishApplicantRestoreWithFeedback = (applicantId, action) => {
    const nextStatuses = statusesForCommittedApplicantAction(action, ["new", "all"]);
    const shouldFlashRestoredCard = applicantFilterKey === "all" || nextStatuses.includes(applicantFilterKey);

    flashApplicantDestination(action);
    applyApplicantActionLocally(applicantId, action);

    if (!shouldFlashRestoredCard) return;
    setApplicantRestoreFeedbackById((prev) => ({ ...prev, [applicantId]: true }));
    window.setTimeout(() => {
      setApplicantRestoreFeedbackById((prev) => {
        const next = { ...prev };
        delete next[applicantId];
        return next;
      });
    }, APPLICANT_MOVE_FEEDBACK_MS);
  };

  const saveApplicantAction = async (
    applicantId,
    action,
    { showMoveFeedback = true, showRestoreFeedback = false, patchOptions = {} } = {},
  ) => {
    const t = String(authToken || "").trim();
    if (!t) throw new Error("Please sign in again to save changes.");
    const jobPostId = String(roleId || "").trim();
    if (!jobPostId) throw new Error("Missing role id.");

    const { application } = await patchHiringJobApplicationStatus(
      jobPostId,
      applicantId,
      action || "new",
      t,
      patchOptions,
    );
    const savedAction = actionFromApplicationStatus(application?.applicationStatus);
    if (showMoveFeedback) {
      finishApplicantMoveWithFeedback(applicantId, savedAction);
    } else if (showRestoreFeedback) {
      finishApplicantRestoreWithFeedback(applicantId, savedAction);
    } else {
      flashApplicantDestination(savedAction);
      applyApplicantActionLocally(applicantId, savedAction);
    }
    return savedAction;
  };

  const persistApplicantAction = async (applicantId, nextAction, patchOptions = {}) => {
    const currentApplicant = roleApplicants.find((applicant) => applicant.id === applicantId);
    const currentAction = applicantActions[applicantId] ?? getInitialApplicantAction(currentApplicant);

    setApplicantActionSavingById((prev) => ({ ...prev, [applicantId]: true }));
    setApplicantActionErrorById((prev) => {
      const next = { ...prev };
      delete next[applicantId];
      return next;
    });
    setApplicantActions((prev) => ({ ...prev, [applicantId]: nextAction }));

    try {
      const savedAction = await saveApplicantAction(applicantId, nextAction, { patchOptions });
      setLastApplicantMove({
        applicantId,
        fromAction: currentAction,
        toAction: savedAction,
      });
      return true;
    } catch (e) {
      applyApplicantActionLocally(applicantId, currentAction);
      setApplicantActionErrorById((prev) => ({
        ...prev,
        [applicantId]: e instanceof Error ? e.message : "Could not save applicant status.",
      }));
      return false;
    } finally {
      setApplicantActionSavingById((prev) => {
        const next = { ...prev };
        delete next[applicantId];
        return next;
      });
    }
  };

  const confirmRejectReason = async () => {
    if (!rejectModalApplicantId) return;
    const applicantId = rejectModalApplicantId;
    setApplicantRejectReasons((prev) => ({ ...prev, [applicantId]: draftRejectReason }));
    const saved = await persistApplicantAction(applicantId, "rejected", {
      rejectionReason: draftRejectReason.trim() || undefined,
      sendRejectionNotification: sendRejectionEmail === true,
    });
    if (saved) closeRejectReasonModal();
  };

  const confirmReportApplicant = async () => {
    if (!reportModalApplicantId) return;
    const applicantId = reportModalApplicantId;
    const explanation = draftReportExplanation.trim();
    if (explanation.length < APPLICANT_REPORT_MIN_EXPLANATION_LENGTH) {
      setApplicantActionErrorById((prev) => ({
        ...prev,
        [applicantId]: `Please add a bit more detail — at least ${APPLICANT_REPORT_MIN_EXPLANATION_LENGTH} characters.`,
      }));
      return;
    }

    const currentApplicant = roleApplicants.find((applicant) => applicant.id === applicantId);
    const currentAction = applicantActions[applicantId] ?? getInitialApplicantAction(currentApplicant);

    setApplicantActionSavingById((prev) => ({ ...prev, [applicantId]: true }));
    setApplicantActionErrorById((prev) => {
      const next = { ...prev };
      delete next[applicantId];
      return next;
    });
    setApplicantActions((prev) => ({ ...prev, [applicantId]: "rejected" }));

    try {
      const t = String(authToken || "").trim();
      const jobPostId = String(roleId || "").trim();
      const { application } = await reportHiringJobApplicant(jobPostId, applicantId, explanation, t);
      const savedAction = actionFromApplicationStatus(application?.applicationStatus) || "rejected";
      finishApplicantMoveWithFeedback(applicantId, savedAction);
      setLastApplicantMove({
        applicantId,
        fromAction: currentAction,
        toAction: savedAction,
      });
      closeReportApplicantModal();
    } catch (e) {
      applyApplicantActionLocally(applicantId, currentAction);
      setApplicantActionErrorById((prev) => ({
        ...prev,
        [applicantId]: e instanceof Error ? e.message : "We couldn't submit your report. Try again in a moment.",
      }));
    } finally {
      setApplicantActionSavingById((prev) => {
        const next = { ...prev };
        delete next[applicantId];
        return next;
      });
    }
  };
  const toggleApplicantAction = async (applicantId, optionKey) => {
    if (applicantActionSavingById[applicantId]) return;
    const currentApplicant = roleApplicants.find((applicant) => applicant.id === applicantId);
    const current = applicantActions[applicantId] ?? getInitialApplicantAction(currentApplicant);
    if (optionKey === "rejected" && current !== "rejected") {
      openRejectReasonModal(applicantId);
      return;
    }
    await persistApplicantAction(applicantId, current === optionKey ? "" : optionKey);
  };

  const handleUndoApplicantMove = async () => {
    if (!lastApplicantMove || undoApplicantMoveSaving) return;
    const { applicantId, fromAction } = lastApplicantMove;
    const currentApplicant = roleApplicants.find((applicant) => applicant.id === applicantId);
    const currentAction = applicantActions[applicantId] ?? getInitialApplicantAction(currentApplicant);

    setUndoApplicantMoveSaving(true);
    setApplicantActionSavingById((prev) => ({ ...prev, [applicantId]: true }));
    setApplicantActionErrorById((prev) => {
      const next = { ...prev };
      delete next[applicantId];
      return next;
    });

    try {
      await saveApplicantAction(applicantId, fromAction, { showMoveFeedback: false, showRestoreFeedback: true });
      setLastApplicantMove(null);
    } catch (e) {
      applyApplicantActionLocally(applicantId, currentAction);
      setApplicantActionErrorById((prev) => ({
        ...prev,
        [applicantId]: e instanceof Error ? e.message : "Could not undo applicant move.",
      }));
    } finally {
      setApplicantActionSavingById((prev) => {
        const next = { ...prev };
        delete next[applicantId];
        return next;
      });
      setUndoApplicantMoveSaving(false);
    }
  };

  if (isUuidRoleId && (isRemoteLoading || remoteRoleError)) {
    return (
      <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
        <main className="nd1-shell nd1-hiring-role-manage-main">
          {remoteRoleError ? (
            <p className="nd1-hiring-empty" style={{ padding: 16 }}>
              {remoteRoleError}
            </p>
          ) : (
            <JobuwinPageLoading
              title="Loading role…"
              subtitle="Fetching role and job post from the database."
            />
          )}
        </main>
      </RecruitmentShell>
    );
  }

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main className="nd1-shell nd1-hiring-role-manage-main">
        <section className="nd1-hiring-role-manage-hero nd1-hiring-role-manage-section">
          <Link className="back-link nd1-recruitment-profile-back" to="/hiring-dashboard" state={location.state || undefined}>
            &larr; Back to hiring dashboard
          </Link>
          <p className="eyebrow nd1-label">Role workspace</p>
          <div className="hiring-role-manage-title-block">
            <h1 className="hiring-role-manage-title">
              <span className="hiring-role-manage-title__text">{role.title}</span>
              <span
                className={`hiring-role-manage-title__status hiring-role-manage-title__status--${roleStatus}`}
                aria-label={`Role status: ${statusLabel}`}
              >
                <HiringRoleStatusGlyph variant={roleStatus} className="hiring-role-manage-title__status-icon" size={14} />
                {statusLabel}
              </span>
            </h1>
            <p className="subtitle hiring-role-manage-subtitle">
              {metaLine ? (
                <span className="hiring-role-manage-meta-item">
                  <MapPin size={16} strokeWidth={1.75} aria-hidden />
                  {metaLine}
                </span>
              ) : null}
              {metaLine && (publishedSubtitle || expirySubtitle) ? (
                <span className="hiring-role-manage-subtitle__sep hiring-role-manage-subtitle__sep--bar" aria-hidden>
                  |
                </span>
              ) : null}
              {publishedSubtitle ? (
                <span className="hiring-role-manage-meta-item">
                  <Clock size={16} strokeWidth={1.75} aria-hidden />
                  Published {publishedSubtitle}
                </span>
              ) : null}
              {publishedSubtitle && expirySubtitle ? (
                <span className="hiring-role-manage-subtitle__sep hiring-role-manage-subtitle__sep--bar" aria-hidden>
                  |
                </span>
              ) : null}
              {expirySubtitle ? (
                <span className="hiring-role-manage-meta-item">
                  <Calendar size={16} strokeWidth={1.75} aria-hidden />
                  Expires {expirySubtitle}
                </span>
              ) : null}
              {hasViewJobLink ? (
                <span className="hiring-role-manage-subtitle__actions">
                  {hasSubtitleMeta ? (
                    <span className="hiring-role-manage-subtitle__sep hiring-role-manage-subtitle__sep--dot" aria-hidden>
                      •
                    </span>
                  ) : null}
                  <Link
                    className="hiring-role-manage-meta-item hiring-role-manage-meta-item--link"
                    to={`/jobs/${isUuidRoleId ? encodeURIComponent(roleId) : listingJobId}`}
                    state={{
                      fromPath: `/hiring/roles/${role.id}`,
                      fromLabel: "Role workspace",
                      returnState: location.state || null,
                    }}
                  >
                    <ExternalLink size={16} strokeWidth={1.75} aria-hidden />
                    View job post
                  </Link>
                </span>
              ) : null}
            </p>
          </div>

          <form className="hiring-role-settings-form hiring-role-settings-form--intro" onSubmit={onSubmitSettings}>
            <div className="hiring-role-manage-status-card">
              <span className="hiring-role-settings-legend" id={`hiring-role-status-label-${role.id}`}>
                Job status
              </span>
              <div
                className="hiring-role-settings-status-controls"
                role="radiogroup"
                aria-labelledby={`hiring-role-status-label-${role.id}`}
              >
                {HIRING_ROLE_STATUSES.map((s) => (
                  <label
                    key={s}
                    className={`hiring-role-status-card hiring-role-status-card--${s}${
                      roleStatus === s ? " hiring-role-status-card--selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      className="hiring-role-status-card__radio"
                      name={`role-status-${role.id}`}
                      checked={roleStatus === s}
                      disabled={isLocked}
                      onChange={() => handleRoleStatusChange(s)}
                    />
                    <span className="hiring-role-status-card__content">
                      <span className="hiring-role-status-card__head">
                        <HiringRoleStatusGlyph variant={s} className="hiring-role-status-card__icon" size={18} />
                        <span className="hiring-role-status-card__title">{hiringRoleStatusLabel(s)}</span>
                      </span>
                      <span className="hiring-role-status-card__desc">{statusDescriptions[s]}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="hiring-role-settings-status-actions">
                <button
                  type="submit"
                  className="nd1-btn nd1-btn--accent hiring-role-status-update-btn"
                  disabled={isLocked || showCloseWarning || !isRoleStatusDirty || settingsSaving}
                >
                  Update status
                </button>
              </div>
            </div>
            {showCloseWarning ? (
              <div className="hiring-role-close-warning" role="alertdialog" aria-modal="true" aria-labelledby="hiring-role-close-warning-title">
                <p id="hiring-role-close-warning-title">
                  Once this role is closed, it cannot be reopened. This will update the listing and recruitment dashboard.
                </p>
                <div className="hiring-role-close-warning-actions">
                  <button
                    type="button"
                    className="nd1-btn nd1-btn--accent"
                    disabled={settingsSaving}
                    onClick={() => {
                      void confirmCloseRole();
                    }}
                  >
                    Confirm
                  </button>
                  <button type="button" className="nd1-btn nd1-btn--outline" onClick={cancelCloseRole}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {saveNotice ? (
              <p
                className={`hiring-role-manage-notice hiring-role-manage-notice--${saveNotice.type}`}
                role={saveNotice.type === "error" ? "alert" : "status"}
              >
                {saveNotice.message}
              </p>
            ) : null}
          </form>
        </section>

        <section className="nd1-hiring-role-manage-applicants nd1-hiring-roles-card">
          <div className="nd1-hiring-section-head">
            <h2 id="hiring-role-applicants-title" className="nd1-hiring-section-head__title">
              Applicants
            </h2>
            <button
              type="button"
              className="hiring-role-applicant-invite-btn"
              onClick={() => setInviteModalOpen(true)}
            >
              + Invite applicants
            </button>
          </div>
          <div className="hiring-role-advert-stats-strip">
            <div className="hiring-role-card-body">
              <HiringRoleApplicantStats
                role={roleForApplicantStats}
                selectable
                defaultSelectedIndex={activeApplicantIconIndex}
                flashIndex={applicantStatsFlashIndex}
                showLabels
                onItemClick={(idx) => setActiveApplicantIconIndex(idx)}
              />
            </div>
          </div>

          <section className="hiring-role-applicant-profiles" aria-label="Applicant profiles">
            <div className="hiring-role-applicant-toolbar">
              <div className="hiring-role-applicant-search">
                <label className="hiring-role-applicant-search__field">
                  <span className="profile-manage-sr-only">Search applicants by name</span>
                  <Search size={18} strokeWidth={1.75} className="hiring-role-applicant-search__icon" aria-hidden />
                  <input
                    type="search"
                    className="hiring-role-applicant-search__input"
                    value={applicantNameQueryDraft}
                    onChange={(event) => setApplicantNameQueryDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      applyApplicantNameSearch();
                    }}
                    placeholder="Search applicants…"
                    autoComplete="off"
                  />
                </label>
                <button
                  type="button"
                  className="hiring-role-applicant-search__btn"
                  onClick={applyApplicantNameSearch}
                >
                  Search
                </button>
              </div>
            </div>

            {applicantsListLoading ? (
              <JobuwinPageLoading
                compact
                title="Loading applicants…"
                subtitle="Fetching applications from the database."
              />
            ) : applicantsListError ? (
              <p className="nd1-hiring-empty" role="alert">
                {applicantsListError}
              </p>
            ) : filteredApplicants.length === 0 ? (
              <p className="nd1-hiring-empty">No applicants in this stage yet.</p>
            ) : (
              <>
                {(() => {
                  const heading = APPLICANT_LIST_HEADINGS[applicantFilterKey] || APPLICANT_LIST_HEADINGS.all;
                  const HeadingIcon = heading.icon;
                  return (
                    <div className="hiring-role-applicant-list-heading">
                      <div className="hiring-role-applicant-list-heading__title">
                        <HeadingIcon size={20} strokeWidth={1.75} aria-hidden />
                        <h3>
                          {heading.label} ({filteredApplicants.length})
                        </h3>
                      </div>
                      <button
                        type="button"
                        className="hiring-role-applicant-undo-btn"
                        aria-label="Undo last action"
                        onClick={handleUndoApplicantMove}
                        disabled={!lastApplicantMove || undoApplicantMoveSaving}
                      >
                        <Undo2 size={18} strokeWidth={1.75} aria-hidden />
                        <span>{undoApplicantMoveSaving ? "Undoing…" : "Undo last action"}</span>
                      </button>
                    </div>
                  );
                })()}
                <div className="hiring-role-applicant-list">
                  {visibleApplicants.map((applicant, index) => {
                    const stageBadge = applicantStageBadgeLabel(applicant);
                    const appliedAtMs = applicantAppliedAtMs(applicant);
                    return (
                      <article
                        key={applicant.id}
                        id={`hiring-role-applicant-list-card-${applicant.id}`}
                        className={`hiring-role-applicant-card hiring-role-applicant-list-card hiring-role-applicant-card--clickable${
                          applicantMoveFeedbackById[applicant.id] ? " hiring-role-applicant-card--moving" : ""
                        }${
                          applicantRestoreFeedbackById[applicant.id] ? " hiring-role-applicant-card--restored" : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${applicant.name}'s full profile`}
                        onClick={() => setDetailApplicantIndex(index)}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          setDetailApplicantIndex(index);
                        }}
                      >
                        <div className="hiring-role-applicant-list-card__layout">
                          <div className="hiring-role-applicant-list-card__media">
                            <ApplicantPitchThumbnail
                              src={applicant.pitchVideoUrl}
                              listVariant
                              playLabel={`Play ${applicant.name}'s pitch video`}
                              onClick={
                                applicant.pitchVideoUrl
                                  ? () => setPitchLightboxApplicant(applicant)
                                  : undefined
                              }
                            />
                          </div>
                          <div className="hiring-role-applicant-list-card__info">
                            <div className="hiring-role-applicant-list-card__head">
                              <div className="hiring-role-applicant-list-card__name-row">
                                <h3>{applicant.name}</h3>
                                {stageBadge ? (
                                  <span
                                    className={`hiring-role-applicant-stage-badge hiring-role-applicant-stage-badge--${stageBadge.toLowerCase()}`}
                                  >
                                    {stageBadge}
                                  </span>
                                ) : null}
                              </div>
                              <time
                                className="hiring-role-applicant-list-card__applied"
                                dateTime={appliedAtMs ? new Date(appliedAtMs).toISOString() : undefined}
                              >
                                {formatApplicantAppliedAtLabel(applicant)}
                              </time>
                            </div>
                            <div className="hiring-role-applicant-list-card__contact">
                              <ApplicantListCompactMeta
                                value={applicant.email}
                                icon={applicantFieldIcons.email}
                              />
                              <ApplicantListCompactMeta
                                value={formatApplicantMobile(applicant)}
                                icon={applicantFieldIcons.mobile}
                              />
                              <ApplicantListCompactMeta
                                value={formatApplicantLocations(applicant)}
                                icon={applicantFieldIcons.location}
                              />
                            </div>
                            <ApplicantProfileLinksRow applicant={applicant} listVariant />
                          </div>
                          <div
                            className="hiring-role-applicant-list-card__actions"
                            role="radiogroup"
                            aria-label="Applicant action"
                          >
                            {APPLICANT_ACTION_OPTIONS.map((option) => (
                              <button
                                key={`${applicant.id}-${option.key}`}
                                type="button"
                                className={`hiring-role-applicant-list-card__action hiring-role-applicant-stage-option${
                                  option.key === "shortlisted"
                                    ? " hiring-role-applicant-list-card__action--shortlist hiring-role-applicant-stage-option--shortlist"
                                    : ""
                                }${
                                  option.key === "parked"
                                    ? " hiring-role-applicant-list-card__action--park hiring-role-applicant-stage-option--park"
                                    : ""
                                }${
                                  option.key === "rejected"
                                    ? " hiring-role-applicant-list-card__action--reject hiring-role-applicant-stage-option--reject"
                                    : ""
                                }${
                                  (applicantActions[applicant.id] ?? getInitialApplicantAction(applicant)) ===
                                  option.key
                                    ? " hiring-role-applicant-list-card__action--selected hiring-role-applicant-stage-option--selected"
                                    : ""
                                }`}
                                aria-pressed={
                                  (applicantActions[applicant.id] ?? getInitialApplicantAction(applicant)) ===
                                  option.key
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleApplicantAction(applicant.id, option.key);
                                }}
                                disabled={Boolean(applicantActionSavingById[applicant.id])}
                              >
                                <span className="hiring-role-applicant-list-card__action-icon" aria-hidden="true">
                                  {option.icon}
                                </span>
                                <span>{option.label}</span>
                              </button>
                            ))}
                            {applicantActionSavingById[applicant.id] ? (
                              <p className="hiring-role-applicant-stage-status">Saving…</p>
                            ) : applicantActionErrorById[applicant.id] ? (
                              <p className="hiring-role-applicant-stage-status hiring-role-applicant-stage-status--error">
                                {applicantActionErrorById[applicant.id]}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  <div
                    ref={attachLoadMoreObserver}
                    className="hiring-role-applicant-load-more-sentinel"
                    aria-hidden="true"
                  />
                </div>

                {!hasMoreApplicants && filteredApplicants.length > 0 ? (
                  <div className="hiring-role-applicant-list-footer">
                    <p className="hiring-role-applicant-list-end">That's everyone — you've reached the end of the results.</p>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </section>

      </main>
      {pitchLightboxApplicant ? (
        <ApplicantPitchLightbox
          applicant={pitchLightboxApplicant}
          onClose={() => setPitchLightboxApplicant(null)}
        />
      ) : null}
      <InviteApplicantsModal
        open={inviteModalOpen}
        onRequestClose={() => {
          setInviteModalOpen(false);
          setInviteModalInitialTab(null);
        }}
        jobActive={roleStatus === "active"}
        jobPostId={String(role?.id || roleId || "")}
        jobTitle={role?.title || ""}
        jobPublicSlug={role?.publicSlug || ""}
        authToken={authToken}
        hiringManagerName={inviteHiringManagerName}
        companyName={inviteCompanyName}
        initialTab={inviteModalInitialTab}
      />
      {detailApplicant ? (
        <div
          className="hiring-role-applicant-detail-overlay"
          role="presentation"
          onClick={() => setDetailApplicantIndex(null)}
        >
          <div
            className="hiring-role-applicant-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${detailApplicant.name} — applicant profile`}
            onClick={(event) => event.stopPropagation()}
          >
            <ApplicantDetailBrowseNav
              applicantTabIndex={detailApplicantIndex}
              totalApplicants={filteredApplicants.length}
              setApplicantTabIndex={setDetailApplicantIndex}
              onClose={() => setDetailApplicantIndex(null)}
            />
            <div className="hiring-role-applicant-detail-modal__body">
              <ApplicantDetailProfile
                applicant={detailApplicant}
                roleTitle={role.title || ""}
                onDownloadCv={() => handleDownloadApplicantCv(detailApplicant)}
                cvDownloadBusy={Boolean(cvDownloadBusyById[detailApplicant.id])}
                applicantMoveFeedbackById={applicantMoveFeedbackById}
                applicantRestoreFeedbackById={applicantRestoreFeedbackById}
              />
            </div>
            <footer className="hiring-role-applicant-detail-modal__footer">
              <div
                className="hiring-role-applicant-detail-modal__actions"
                role="radiogroup"
                aria-label="Applicant action"
              >
                {APPLICANT_ACTION_OPTIONS.map((option) => (
                  <button
                    key={`${detailApplicant.id}-detail-${option.key}`}
                    type="button"
                    className={`hiring-role-applicant-list-card__action hiring-role-applicant-stage-option${
                      option.key === "shortlisted"
                        ? " hiring-role-applicant-list-card__action--shortlist hiring-role-applicant-stage-option--shortlist"
                        : ""
                    }${
                      option.key === "parked"
                        ? " hiring-role-applicant-list-card__action--park hiring-role-applicant-stage-option--park"
                        : ""
                    }${
                      option.key === "rejected"
                        ? " hiring-role-applicant-list-card__action--reject hiring-role-applicant-stage-option--reject"
                        : ""
                    }${
                      (applicantActions[detailApplicant.id] ?? getInitialApplicantAction(detailApplicant)) ===
                      option.key
                        ? " hiring-role-applicant-list-card__action--selected hiring-role-applicant-stage-option--selected"
                        : ""
                    }`}
                    aria-pressed={
                      (applicantActions[detailApplicant.id] ?? getInitialApplicantAction(detailApplicant)) ===
                      option.key
                    }
                    onClick={() => toggleApplicantAction(detailApplicant.id, option.key)}
                    disabled={Boolean(applicantActionSavingById[detailApplicant.id])}
                  >
                    <span className="hiring-role-applicant-list-card__action-icon" aria-hidden="true">
                      {option.icon}
                    </span>
                    <span>{option.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="hiring-role-applicant-list-card__action hiring-role-applicant-list-card__action--report hiring-role-applicant-stage-option hiring-role-applicant-stage-option--report"
                  onClick={() => openReportApplicantModal(detailApplicant.id)}
                  disabled={Boolean(applicantActionSavingById[detailApplicant.id])}
                >
                  <span className="hiring-role-applicant-list-card__action-icon" aria-hidden="true">
                    <Flag {...STAGE_ACTION_ICON_PROPS} />
                  </span>
                  <span>Report</span>
                </button>
              </div>
              {applicantActionSavingById[detailApplicant.id] ? (
                <p className="hiring-role-applicant-stage-status">Saving…</p>
              ) : applicantActionErrorById[detailApplicant.id] ? (
                <p className="hiring-role-applicant-stage-status hiring-role-applicant-stage-status--error">
                  {applicantActionErrorById[detailApplicant.id]}
                </p>
              ) : null}
            </footer>
          </div>
        </div>
      ) : null}
      {rejectModalApplicantId && typeof document !== "undefined"
        ? createPortal(
            <div
              className="nd1-root nd1-root--hiring nd1-hiring-app jw-recruitment-page hiring-role-reject-overlay"
              role="presentation"
              onClick={() => {
                if (!applicantActionSavingById[rejectModalApplicantId]) closeRejectReasonModal();
              }}
            >
              <div
                className="hiring-role-reject-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hiring-role-reject-title"
                onClick={(event) => event.stopPropagation()}
              >
                <h3 id="hiring-role-reject-title">Select reject reason</h3>
                <p className="saved-search-meta">Choose one reason to mark this applicant as rejected.</p>
                <div className="hiring-role-reject-options" role="radiogroup" aria-label="Reject reason options">
                  {rejectReasonOptions.map((reason) => (
                    <label key={reason} className="hiring-role-reject-option">
                      <input
                        type="radio"
                        className="checkbox-control checkbox-control--primary-2"
                        name="hiring-reject-reason"
                        checked={draftRejectReason === reason}
                        onChange={() => setDraftRejectReason(reason)}
                        disabled={Boolean(applicantActionSavingById[rejectModalApplicantId])}
                      />
                      <span>{reason}</span>
                    </label>
                  ))}
                </div>
                <div className="hiring-role-reject-secondary-options">
                  <p className="hiring-role-reject-secondary-title">Additional actions</p>
                  <label className="hiring-role-reject-secondary-option">
                    <input
                      type="checkbox"
                      className="checkbox-control checkbox-control--primary-2"
                      checked={sendRejectionEmail}
                      onChange={(event) => setSendRejectionEmail(event.target.checked)}
                      disabled={Boolean(applicantActionSavingById[rejectModalApplicantId])}
                    />
                    <span>Send the rejection notification</span>
                  </label>
                </div>
                {applicantActionErrorById[rejectModalApplicantId] ? (
                  <p className="hiring-role-applicant-stage-status hiring-role-applicant-stage-status--error">
                    {applicantActionErrorById[rejectModalApplicantId]}
                  </p>
                ) : null}
                <div className="hiring-role-reject-actions">
                  <button
                    type="button"
                    className="hiring-role-reject-cancel-btn"
                    onClick={closeRejectReasonModal}
                    disabled={Boolean(applicantActionSavingById[rejectModalApplicantId])}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="large-filled-button"
                    onClick={confirmRejectReason}
                    disabled={Boolean(applicantActionSavingById[rejectModalApplicantId])}
                  >
                    {applicantActionSavingById[rejectModalApplicantId] ? "Saving..." : "Confirm Reject"}
                  </button>
                </div>
                <p className="hiring-role-reject-manage">
                  <a
                    href="/response-settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hiring-role-reject-manage-link"
                  >
                    Manage reject comms (new window)
                  </a>
                </p>
              </div>
            </div>,
            document.body
          )
        : null}
      {reportModalApplicantId && typeof document !== "undefined"
        ? createPortal(
            <div
              className="nd1-root nd1-root--hiring nd1-hiring-app jw-recruitment-page hiring-role-reject-overlay hiring-role-report-overlay"
              role="presentation"
            >
              <div
                className="hiring-role-reject-modal hiring-role-report-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hiring-role-report-title"
              >
                <h3 id="hiring-role-report-title">Report this applicant</h3>
                <p className="hiring-role-report-modal__intro">
                  We take every report seriously — we'll look into this and follow up if we need to.
                </p>
                <p className="hiring-role-report-modal__intro">
                  All reported candidates are automatically rejected.
                </p>
                <label className="hiring-role-report-modal__field" htmlFor="hiring-role-report-explanation">
                  <span className="hiring-role-report-modal__label">What happened?</span>
                  <textarea
                    id="hiring-role-report-explanation"
                    className="hiring-role-report-modal__textarea"
                    rows={4}
                    value={draftReportExplanation}
                    onChange={(event) => setDraftReportExplanation(event.target.value)}
                    placeholder="Tell us briefly why you're reporting this applicant…"
                    disabled={Boolean(applicantActionSavingById[reportModalApplicantId])}
                  />
                </label>
                {applicantActionErrorById[reportModalApplicantId] ? (
                  <p className="hiring-role-applicant-stage-status hiring-role-applicant-stage-status--error">
                    {applicantActionErrorById[reportModalApplicantId]}
                  </p>
                ) : null}
                <div className="hiring-role-reject-actions">
                  <button
                    type="button"
                    className="hiring-role-reject-cancel-btn"
                    onClick={closeReportApplicantModal}
                    disabled={Boolean(applicantActionSavingById[reportModalApplicantId])}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="large-filled-button hiring-role-report-modal__submit"
                    onClick={confirmReportApplicant}
                    disabled={
                      Boolean(applicantActionSavingById[reportModalApplicantId]) ||
                      draftReportExplanation.trim().length < APPLICANT_REPORT_MIN_EXPLANATION_LENGTH
                    }
                  >
                    {applicantActionSavingById[reportModalApplicantId] ? "Submitting…" : "Submit report"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </RecruitmentShell>
  );
}
